import type { IssueTypePriority, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	IssuePriorityEqualsFilterModifier,
	IssuePriorityExcludesFilterModifier,
	IssuePriorityIncludesFilterModifier,
	IssuePriorityNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { IssuePriorityFilterCell } from "./IssuePriorityFilterCell"

type IssuePriorityFieldValue = IssueTypePriority["uuid"]
type IssuePriorityFilterValue = IssueTypePriority["uuid"][] | IssueTypePriority["uuid"]

export class IssuePriorityFilterDef extends BaseFilterDef<IssuePriorityFieldValue, IssuePriorityFilterValue> {
	readonly id = "issue-priority"
	readonly defaultFilterValue: IssuePriorityFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly issueTypePriorities: Stored<IssueTypePriority>[]

	constructor(issueTypePriorities: Stored<IssueTypePriority>[]) {
		super()
		this.issueTypePriorities = issueTypePriorities
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: IssuePriorityEqualsFilterModifier,
			isValueValid: (_value: IssuePriorityFieldValue): _value is IssueTypePriority["uuid"] => true,
			isFilterValueValid: (filterValue: IssuePriorityFilterValue): filterValue is IssueTypePriority["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: IssuePriorityNotEqualsFilterModifier,
			isValueValid: (_value: IssuePriorityFieldValue): _value is IssueTypePriority["uuid"] => true,
			isFilterValueValid: (filterValue: IssuePriorityFilterValue): filterValue is IssueTypePriority["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: IssuePriorityIncludesFilterModifier,
			isValueValid: (_value: IssuePriorityFieldValue): _value is IssueTypePriority["uuid"] => true,
			isFilterValueValid: (filterValue: IssuePriorityFilterValue): filterValue is IssueTypePriority["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: IssuePriorityExcludesFilterModifier,
			isValueValid: (_value: IssuePriorityFieldValue): _value is IssueTypePriority["uuid"] => true,
			isFilterValueValid: (filterValue: IssuePriorityFilterValue): filterValue is IssueTypePriority["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<IssuePriorityFilterDef>): ReactNode {
		return <IssuePriorityFilterCell key={props.filter.id} {...props} />
	}
}
